import { clsx } from 'clsx'
import { RAIL, STAFF, prepProgress, type MissingField, type SectionId } from '@sonder/shared'
import { doc, updateDoc } from 'firebase/firestore'
import { useEffect, useMemo } from 'react'
import { Link, Outlet, useLocation } from 'react-router'
import { useMode } from '@/app/mode'
import { useAuth } from '@/auth/AuthProvider'
import { enabledSections, useAllSections, useBrandCount, useProgress } from '@/data/client'
import { toFieldPath, useDoc } from '@/data/hooks'
import { db } from '@/lib/firebase'
import { PeopleNamesProvider } from '@/data/review'
import { setPreviewReturn } from '@/staff/previewReturn'
import { ProgressNumber, ProgressRow, StepRail, type Step } from '@/ui'
import { TopBar } from './TopBar'
import type { PageProps } from './ClientRoutes'

/** Handed to the section pages through the router outlet. `progress` is the rail's headline count, for their bottom bar. */
export type WorkspaceContext = { missing: (id: SectionId) => MissingField[]; progress: { done: number; total: number; label: string; ready: boolean } }

export function WorkspaceLayout({ clientId, client, config, base }: PageProps) {
  const { docs, loading } = useAllSections(clientId)
  const progress = useProgress(config, docs)
  const sections = enabledSections(config, useBrandCount(clientId))
  const location = useLocation()
  const { user, impersonation } = useAuth()
  const clientMode = useMode() === 'client'
  // Reaching the onboarding means the introduction is done: from now on their address opens the home page.
  // Not when an admin is signed in as them: their record stays theirs (the rules refuse it too).
  const me = useDoc<{ introSeen?: Record<string, number> }>(clientMode && user && !impersonation ? `users/${user.uid}` : null)
  const seen = me.loading || Boolean(me.data?.introSeen?.[clientId])
  useEffect(() => {
    if (!clientMode || !user || impersonation || seen) return
    updateDoc(doc(db, `users/${user.uid}`), toFieldPath(`introSeen.${clientId}`), Date.now()).catch(() => undefined)
  }, [clientMode, user, impersonation, seen, clientId])
  // Staff prefilling measure what they have filled in; the client (and the preview of their view)
  // measures what the client has confirmed. The two only meet once the client has reviewed everything.
  const prep = useMode() === 'prep'
  // Staff count only what Sonder fills; access steps are the client's.
  const prepped = useMemo(() => (prep && !loading ? prepProgress(config, docs) : null), [prep, loading, config, docs])
  const prepState = (id: SectionId): Step['state'] => {
    const p = prepped?.sections[id]
    if (!p || p.total === 0 || p.filled === 0) return 'todo'
    return p.filled >= p.total ? 'done' : 'partial'
  }
  // What staff still have to fill, per section; the rail counts it and the section page lists it.
  const missing = (id: SectionId): MissingField[] => prepped?.sections[id]?.missing ?? []
  const toFill = (id: SectionId) => {
    const n = missing(id).length
    return n > 0 ? `${n} to fill` : undefined
  }
  const steps: Step[] = [
    ...sections.map((s) => ({ id: s.id, number: s.number, title: s.title, href: `${base}/onboarding/${s.id}`, state: prep ? prepState(s.id) : ((progress?.states[s.id] ?? 'todo') as Step['state']), note: prep ? toFill(s.id) : undefined })),
    { id: 'review', number: String(sections.length + 1).padStart(2, '0'), title: 'Review & submit', href: `${base}/onboarding/review`, state: client.status === 'submitted' ? 'done' : 'todo' },
  ]
  const overall = progress?.overall
  // The same headline count the rail shows: what Sonder has filled in while prefilling, otherwise what the client has done.
  const headline: WorkspaceContext['progress'] = prep
    ? { done: prepped?.overall.filled ?? 0, total: prepped?.overall.total ?? 0, label: STAFF.prepFilledLabel, ready: prepped !== null && progress !== null }
    : { done: overall?.done ?? 0, total: overall?.total ?? 0, label: 'Onboarding progress', ready: !loading && progress !== null }
  return (
    <>
      <TopBar clientName={client.name} section="Onboarding" homeHref={base} />
      <div className="mx-auto grid w-full max-w-portal grid-cols-[minmax(0,1fr)] gap-8 px-6 pt-6 pb-8 md:px-10 lg:grid-cols-[17.5rem_minmax(0,1fr)] lg:gap-16 lg:pt-8">
        <aside className="grid min-w-0 content-start gap-5 lg:sticky lg:gap-8 lg:top-[calc(var(--topbar-h,4rem)+2rem)] lg:self-start lg:max-h-[calc(100dvh-var(--topbar-h,4rem)-4rem)] lg:overflow-y-auto lg:-mx-1 lg:px-1 [scrollbar-width:thin]">
          {prep ? (
            // Two separate counts: what Sonder has filled in, then what the client has reviewed of it.
            <div className={clsx('grid gap-6', (!prepped || !progress) && 'invisible')}>
              <div className="grid gap-3">
                <p className="text-eyebrow uppercase tracking-eyebrow text-ink">{STAFF.prepFilledLabel}</p>
                <ProgressNumber done={prepped?.overall.filled ?? 0} total={prepped?.overall.total ?? 0} label={STAFF.prepFilledLabel} ready={prepped !== null && progress !== null} />
                <p className="text-small text-dim">{STAFF.prepProgressNote}</p>
              </div>
              <ProgressRow done={overall?.done ?? 0} total={overall?.total ?? 0} label={STAFF.prepConfirmedLabel} />
            </div>
          ) : (
            <ProgressNumber done={overall?.done ?? 0} total={overall?.total ?? 0} label="Onboarding progress" ready={!loading && progress !== null} />
          )}
          <StepRail steps={steps} />
          {prep ? (
            <div className="hidden gap-2 border-t border-gray-200 pt-6 lg:grid">
              <p className="text-eyebrow uppercase tracking-eyebrow text-ink">{STAFF.prepRailTitle}</p>
              <p className="text-small text-dim">{STAFF.prepRailBody}</p>
              <Link
                to={location.pathname.replace(`/clients/${clientId}/prep`, `/clients/${clientId}/preview`)}
                onClick={() => setPreviewReturn(location.pathname)}
                className="justify-self-start text-small underline underline-offset-4 hover:text-ink"
              >
                {STAFF.prepRailPreview}
              </Link>
              <Link to={`/staff/clients/${clientId}/people`} className="justify-self-start text-small underline underline-offset-4 hover:text-ink">
                {STAFF.prepRailInvite}
              </Link>
            </div>
          ) : (
            <div className="hidden gap-2 border-t border-gray-200 pt-6 lg:grid">
              <p className="text-eyebrow uppercase tracking-eyebrow text-ink">{RAIL.helpTitle}</p>
              <p className="text-small text-dim">{RAIL.helpBody}</p>
              <Link to={`${base}/people`} className="text-small underline underline-offset-4 hover:text-ink">
                {RAIL.invite}
              </Link>
            </div>
          )}
        </aside>
        <main id="main" className="min-w-0">
          <PeopleNamesProvider clientId={clientId}>
            <Outlet context={{ missing, progress: headline } satisfies WorkspaceContext} />
          </PeopleNamesProvider>
        </main>
      </div>
    </>
  )
}
